<script setup>
import Cookies from 'js-cookie';

import { computed, onMounted, ref } from 'vue';
import { useStore } from 'vuex';
import {
  AlertBox,
  FeatureSummary,
  IconAction,
  LoadingOverlay,
  MapCanvas,
  UserGuide,
} from '@/app/components';
import { pushToDataLayer } from '@/ui/scripts/analytics/analytics.methods';

const store = useStore();

const mq = window.matchMedia('(min-width: 61.25em)');

const guideCta = ref(null);
const guideEl = ref(null);
const mapEl = ref(null);
const summaryEl = ref(null);
const isDesktop = ref(false);

const hasSelectedFeature = computed(() => store.getters['projectsMap/hasSelectedFeature']);
const userGuideVisible = computed(() => store.state.projectsMap.userGuideVisible);
const inertMap = computed(() => {
  if (userGuideVisible.value && isDesktop.value === true) {
    return '';
  }
  return null;
});
const activeCssClass = computed(() => {
  return { 'is-active': hasSelectedFeature.value === true || userGuideVisible.value === true };
});
const layerError = computed(() => store.state.projectsMap.layerError);

function onAfterEnterGuide() {
  guideEl.value.focusEl.focus();
  mapEl.value.updateMapSize();
}

function onAfterLeaveGuide() {
  guideCta.value.$el.focus();
}

function onAfterEnterSummary() {
  summaryEl.value.focusEl.focus();
  mapEl.value.updateMapSize();
}

function onAfterLeaveSummary() {
  mapEl.value.$el.focus();
}

function onBeforeLeave() {
  mapEl.value.updateMapSize();
}

function openUserGuide() {
  store.dispatch('projectsMap/toggleUserGuideVisibility', true);
}

function gtmProjectMapUserGuideTrigger(action) {
  pushToDataLayer(
    'projectMapUserGuide_tracking',
    'projectMapUserGuide_action',
    'projectMapUserGuide_label',
    `Click - User Guide`,
    action,
  );
}

function screenSizeHandler() {
  isDesktop.value = window.matchMedia('(min-width: 61.25em)').matches;
}

onMounted(() => {
  if (!Cookies.get('projectMapUserGuideVisited')) {
    openUserGuide();
  }
  screenSizeHandler();
  mq.addEventListener('change', screenSizeHandler);
});

defineExpose({
  mapEl,
});
</script>

<template>
  <div class="c-map-wrapper">
    <div :class="activeCssClass" class="c-map-wrapper__wrapper">
      <div role="alert" aria-live="polite">
        <Transition name="c-map-wrapper__error-panel-">
          <AlertBox v-if="layerError" :positioned="true">
            <p v-html="layerError" />
          </AlertBox>
        </Transition>
      </div>
      <div :inert="inertMap" class="c-map-wrapper__return-action">
        <slot />
      </div>
      <MapCanvas :class="activeCssClass" :inert="inertMap" ref="mapEl" />
      <div :inert="inertMap" class="c-map-wrapper__guide-action">
        <IconAction
          @click="
            openUserGuide();
            gtmProjectMapUserGuideTrigger('Open');
          "
          ref="guideCta"
        />
      </div>
      <Transition
        @after-enter="onAfterEnterGuide"
        @after-leave="onAfterLeaveGuide"
        @before-leave="onBeforeLeave"
        name="c-map-wrapper__guide-panel-"
      >
        <UserGuide v-if="userGuideVisible" ref="guideEl" />
      </Transition>
      <Transition
        @after-enter="onAfterEnterSummary"
        @after-leave="onAfterLeaveSummary"
        @before-leave="onBeforeLeave"
        name="c-map-wrapper__summary-panel-"
      >
        <FeatureSummary v-if="hasSelectedFeature" ref="summaryEl" />
      </Transition>
    </div>
  </div>
  <LoadingOverlay />
</template>

<style lang="scss">
@import 'sass-mq';
@import '@/ui/styles/functions/functions';
@import '@/ui/styles/mixins/layouts';
@import '@/ui/styles/mixins/map-panels';
@import '@/ui/styles/settings/breakpoints';
@import '@/ui/styles/settings/colors';
@import '@/ui/styles/settings/units';

.c-map-wrapper {
  @include map-height;
  position: relative;
  width: 100%;
  overflow: hidden;
}

.c-map-wrapper__return-action {
  position: absolute;
  top: 100%;
  z-index: 1;
  display: flex;
  justify-content: center;
  padding-bottom: $spacing-x-large;
  width: 100%;
  transition: top 450ms cubic-bezier(0.45, 0, 0.55, 1);
  transform: translateY(-100%);

  .is-active & {
    @include mq($until: large) {
      top: 18.75rem;
    }
  }
}

.c-map-wrapper__guide-action {
  position: absolute;
  top: $spacing-x-large;
  left: $spacing-x-large;
  z-index: 1;
}

.c-map-wrapper__wrapper {
  position: relative;
  height: 100%;
  width: 100%;

  @include mq($until: large) {
    overflow: hidden;
  }
}

.c-map-wrapper__error-panel--leave-active,
.c-map-wrapper__error-panel--enter-active {
  transition: opacity 450ms cubic-bezier(0.45, 0, 0.55, 1);
}

.c-map-wrapper__error-panel--enter-from,
.c-map-wrapper__error-panel--leave-to {
  opacity: 0;
}

.c-map-wrapper__guide-panel--leave-active,
.c-map-wrapper__guide-panel--enter-active,
.c-map-wrapper__summary-panel--leave-active,
.c-map-wrapper__summary-panel--enter-active {
  transition: opacity 200ms cubic-bezier(0.45, 0, 0.55, 1);
}

.c-map-wrapper__guide-panel--enter-from,
.c-map-wrapper__guide-panel--leave-to {
  @include mq($from: large) {
    opacity: 0;
  }
}

.c-map-wrapper__summary-panel--enter-from,
.c-map-wrapper__summary-panel--leave-to {
  @include mq($from: large) {
    transform: translateX(100%);
  }
}

// override - force zoom buttons over back button
.ol-overlaycontainer-stopevent {
  z-index: 1 !important;
}
</style>
